import React from "react";
import { Tooltip, Chip, Box, IconButton } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { ChatSource, ChatAttachmentSource } from "@services/chat/types";

/**
 * Truncate text to a maximum length
 */
function truncateText(text: string, maxLength: number = 30): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + "...";
}

/**
 * Get label for source chip
 */
export function getSourceLabel(source: ChatSource): string {
  if (source.type === "email") {
    return truncateText(source.data.subject, 25);
  } else if (source.type === "attachment") {
    const filename = source.data.filename;
    // Handle email attachments that show as "unnamed" or have no proper filename
    if (!filename || filename === "unnamed" || filename.trim() === "") {
      // Check if it's an email attachment by content type
      const contentType = source.data.contentType?.toLowerCase() || "";
      if (
        contentType.includes("message/rfc822") ||
        contentType.includes("email")
      ) {
        // Use parent email subject for email attachments
        return truncateText(source.data.emailSubject, 25);
      }
      return "Attachment";
    }
    return truncateText(filename, 25);
  }
  return "Source";
}

/**
 * Get tooltip text for source
 */
export function getSourceTooltip(source: ChatSource): string {
  if (source.type === "email") {
    return source.data.subject;
  } else if (source.type === "attachment") {
    const filename = source.data.filename;
    // Handle email attachments that show as "unnamed"
    if (!filename || filename === "unnamed" || filename.trim() === "") {
      const contentType = source.data.contentType?.toLowerCase() || "";
      if (
        contentType.includes("message/rfc822") ||
        contentType.includes("email")
      ) {
        return `Email attachment (from: ${source.data.emailSubject})`;
      }
      return `Attachment (from: ${source.data.emailSubject})`;
    }
    return `${filename} (from: ${source.data.emailSubject})`;
  }
  return "Source";
}

/**
 * Render a text segment, handling newlines
 */
export function renderTextWithNewlines(
  text: string,
  keyPrefix: string,
): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const lines = text.split("\n");

  lines.forEach((line, i) => {
    if (i > 0) {
      parts.push(<br key={`${keyPrefix}-br-${i}`} />);
    }
    if (line) {
      parts.push(
        <React.Fragment key={`${keyPrefix}-text-${i}`}>{line}</React.Fragment>,
      );
    }
  });

  return parts;
}

// Light blue color scheme
const CHIP_COLORS = {
  background: "rgba(66, 165, 245, 0.1)",
  border: "rgba(66, 165, 245, 0.4)",
  text: "rgb(21, 101, 192)",
  hoverBackground: "rgba(66, 165, 245, 0.2)",
};

/**
 * Render an email citation chip
 */
function renderEmailChip(
  source: ChatSource,
  partKey: number,
  onOpenEmail: (source: ChatSource) => void,
): React.ReactNode {
  const label = getSourceLabel(source);
  const tooltip = getSourceTooltip(source);

  return (
    <Tooltip key={`citation-${partKey}`} title={tooltip} arrow placement="top">
      <Chip
        icon={<EmailIcon />}
        label={label}
        size="small"
        onClick={(e) => {
          e.preventDefault();
          onOpenEmail(source);
        }}
        sx={{
          height: "22px",
          fontSize: "0.75rem",
          cursor: "pointer",
          mx: 0.25,
          my: 0.25,
          verticalAlign: "middle",
          backgroundColor: CHIP_COLORS.background,
          borderColor: CHIP_COLORS.border,
          color: CHIP_COLORS.text,
          border: "1px solid",
          "& .MuiChip-icon": {
            fontSize: "0.9rem",
            color: "inherit",
          },
          "& .MuiChip-label": {
            px: 0.75,
          },
          "&:hover": {
            backgroundColor: CHIP_COLORS.hoverBackground,
          },
        }}
      />
    </Tooltip>
  );
}

/**
 * Render an attachment citation chip with download and separate open email link
 */
function renderAttachmentChip(
  source: ChatAttachmentSource,
  partKey: number,
  onOpenEmail: (source: ChatSource) => void,
  onDownloadAttachment?: (source: ChatAttachmentSource) => void,
): React.ReactNode {
  const label = getSourceLabel(source);
  const downloadTooltip = `Download: ${source.data.filename}`;
  const openEmailTooltip = `Show email: ${source.data.emailSubject}`;

  return (
    <Box
      key={`citation-${partKey}`}
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
        flexWrap: "wrap",
        mx: 0.25,
        my: 0.25,
        gap: 0.5,
        maxWidth: "100%",
      }}
    >
      <Tooltip title={downloadTooltip} arrow placement="top">
        <Chip
          icon={<AttachFileIcon />}
          label={label}
          size="small"
          onClick={(e) => {
            e.preventDefault();
            if (onDownloadAttachment) {
              onDownloadAttachment(source);
            }
          }}
          sx={{
            height: "22px",
            maxWidth: "100%",
            fontSize: "0.75rem",
            cursor: onDownloadAttachment ? "pointer" : "default",
            backgroundColor: CHIP_COLORS.background,
            borderColor: CHIP_COLORS.border,
            color: CHIP_COLORS.text,
            border: "1px solid",
            "& .MuiChip-icon": {
              fontSize: "0.9rem",
              color: "inherit",
            },
            "& .MuiChip-label": {
              px: 0.75,
            },
            "&:hover": {
              backgroundColor: onDownloadAttachment
                ? CHIP_COLORS.hoverBackground
                : CHIP_COLORS.background,
            },
          }}
        />
      </Tooltip>
      <Tooltip title={openEmailTooltip} arrow placement="top">
        <Box
          component="span"
          onClick={(e) => {
            e.preventDefault();
            onOpenEmail(source);
          }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.25,
            cursor: "pointer",
            color: CHIP_COLORS.text,
            fontSize: "0.7rem",
            whiteSpace: "nowrap",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          <OpenInNewIcon sx={{ fontSize: "0.8rem" }} />
          <span>show email</span>
        </Box>
      </Tooltip>
    </Box>
  );
}

/**
 * Options for rendering citations
 */
export interface CitationRenderOptions {
  onOpenEmail: (source: ChatSource) => void;
  onDownloadAttachment?: (source: ChatAttachmentSource) => void;
}

/**
 * Render message content with inline clickable citations
 */
export function renderMessageWithCitations(
  content: string,
  sources: ChatSource[],
  onSourceClick: (source: ChatSource) => void,
  onDownloadAttachment?: (source: ChatAttachmentSource) => void,
): React.ReactNode {
  // Build a map from citation raw string to source
  // The sources array is in the same order as unique citations appear in text
  const citationToSourceMap = new Map<string, ChatSource>();

  // Get unique citations in order of first appearance
  const uniqueCitations: string[] = [];
  const seen = new Set<string>();

  // Use a fresh regex instance to avoid state issues
  const regex = /<email-(\d+)(?:,attachment-(\d+))?>/g;
  let match;

  while ((match = regex.exec(content)) !== null) {
    const raw = match[0];
    if (!seen.has(raw)) {
      seen.add(raw);
      uniqueCitations.push(raw);
    }
  }

  // Map unique citations to sources in order
  for (let i = 0; i < uniqueCitations.length && i < sources.length; i++) {
    citationToSourceMap.set(uniqueCitations[i], sources[i]);
  }

  // Split content by citation tokens and rebuild with clickable chips
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let partKey = 0;

  // Use a fresh regex instance
  const splitRegex = /<email-(\d+)(?:,attachment-(\d+))?>/g;

  while ((match = splitRegex.exec(content)) !== null) {
    // Add text before the citation (with newline handling)
    if (match.index > lastIndex) {
      const textBefore = content.substring(lastIndex, match.index);
      parts.push(...renderTextWithNewlines(textBefore, `part-${partKey++}`));
    }

    // Add the citation as a clickable chip
    const citationRaw = match[0];
    const source = citationToSourceMap.get(citationRaw);

    if (source) {
      if (source.type === "attachment") {
        parts.push(
          renderAttachmentChip(
            source,
            partKey++,
            onSourceClick,
            onDownloadAttachment,
          ),
        );
      } else {
        parts.push(renderEmailChip(source, partKey++, onSourceClick));
      }
    }
    // No source found - hide the raw citation token

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last citation (with newline handling)
  if (lastIndex < content.length) {
    const textAfter = content.substring(lastIndex);
    parts.push(...renderTextWithNewlines(textAfter, `part-${partKey++}`));
  }

  return <>{parts}</>;
}
